import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/* ------------------------------------------------------------------ classes */

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* -------------------------------------------------------------------- cards */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-lg border border-edge bg-panel', className)}>{children}</div>
  );
}

export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'good' | 'bad' | 'warn';
}) {
  const toneClass =
    tone === 'good'
      ? 'text-emerald-400'
      : tone === 'bad'
        ? 'text-red-400'
        : tone === 'warn'
          ? 'text-amber-400'
          : 'text-slate-100';
  return (
    <div className="rounded-lg border border-edge bg-panel px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn('mt-1 text-lg font-semibold tabular-nums', toneClass)}>{value}</div>
      {sub != null && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ badges */

type BadgeTone = 'neutral' | 'good' | 'bad' | 'warn' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-slate-700/40 text-slate-300 border-slate-600/50',
  good: 'bg-emerald-500/15 text-emerald-300 border-emerald-600/40',
  bad: 'bg-red-500/15 text-red-300 border-red-600/40',
  warn: 'bg-amber-500/15 text-amber-300 border-amber-600/40',
  info: 'bg-sky-500/15 text-sky-300 border-sky-600/40',
};

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: BadgeTone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium',
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function toneForSeverity(sev: string): BadgeTone {
  const s = sev.toLowerCase();
  if (s === 'critical' || s === 'error') return 'bad';
  if (s === 'warning' || s === 'warn') return 'warn';
  if (s === 'info') return 'info';
  return 'neutral';
}

export function toneForModelState(state: string): BadgeTone {
  if (state === 'approved') return 'good';
  if (state === 'rejected') return 'bad';
  if (state === 'archived') return 'neutral';
  if (state === 'paper_test' || state === 'validated') return 'info';
  return 'warn';
}

/* --------------------------------------------------------------- buttons */

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  size = 'md',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  size?: 'sm' | 'md';
}) {
  const base =
    'inline-flex items-center justify-center rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const sizing = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  const variants = {
    default: 'border border-edge bg-surface text-slate-200 hover:border-slate-500',
    primary: 'bg-slate-200 text-slate-900 hover:bg-white',
    danger: 'bg-red-600 text-white hover:bg-red-500',
    ghost: 'text-slate-400 hover:text-slate-200',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cn(base, sizing, variants[variant])}>
      {children}
    </button>
  );
}

export function ConfirmButton({
  children,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  variant = 'default',
  disabled,
  needsReason,
}: {
  children: ReactNode;
  onConfirm: (reason: string) => Promise<unknown> | unknown;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  variant?: 'default' | 'danger' | 'primary';
  disabled?: boolean;
  needsReason?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(reason.trim());
      setOpen(false);
      setReason('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'action failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button size="sm" variant={variant} disabled={disabled} onClick={() => setOpen(true)}>
        {children}
      </Button>
      {open && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-edge bg-panel p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
            {body != null && <div className="mt-2 text-sm text-slate-400">{body}</div>}
            {needsReason && (
              <input
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (required, audited)"
                className="mt-3 w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500"
              />
            )}
            {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant={variant === 'default' ? 'primary' : variant}
                disabled={busy || (needsReason && reason.trim().length === 0)}
                onClick={run}
              >
                {busy ? 'Working…' : confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(
          () => {
            setDone(true);
            setTimeout(() => setDone(false), 1200);
          },
          () => undefined,
        );
      }}
    >
      {done ? 'Copied' : label}
    </Button>
  );
}

/* ---------------------------------------------------------------- states */

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <p className="py-8 text-sm text-slate-400">{label}</p>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <p role="alert" className="rounded border border-red-900 bg-red-950/60 px-3 py-2 text-sm text-red-300">
      {message}
    </p>
  );
}

export function EmptyBox({ children = 'Nothing here yet.' }: { children?: ReactNode }) {
  return (
    <div className="rounded border border-dashed border-edge px-4 py-10 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}

/* ----------------------------------------------------------------- table */

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-edge">
      <table className="w-full min-w-[40rem] text-left text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'border-b border-edge bg-panel px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cn('border-b border-edge/60 px-3 py-2 align-top', className)}>{children}</td>;
}

export function Pagination({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex items-center justify-between pt-3 text-xs text-slate-500">
      <span>
        {from}–{to} of {total}
      </span>
      <div className="flex gap-2">
        <Button size="sm" disabled={offset <= 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          Prev
        </Button>
        <Button size="sm" disabled={to >= total} onClick={() => onChange(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- json view */

export function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="overflow-x-auto rounded border border-edge bg-surface p-3 text-xs text-slate-300">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/* ------------------------------------------------------ query boundary */

interface QueryLike {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export function QueryBoundary({
  q,
  children,
  empty,
  isEmpty,
  loadingLabel,
}: {
  q: QueryLike;
  children: ReactNode;
  empty?: ReactNode;
  isEmpty?: boolean;
  loadingLabel?: string;
}) {
  if (q.isLoading) return <Spinner label={loadingLabel} />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (isEmpty) return <>{empty ?? <EmptyBox />}</>;
  return <>{children}</>;
}

/* ------------------------------------------------------------ search select */

export interface Choice {
  value: string;
  label: string;
  /** Second line, and also searched — an account's broker/login, a symbol's
   *  broker name. More context in the list is what makes a long one usable. */
  hint?: string;
  /** Right-aligned marker, e.g. "not mapped". */
  tag?: string;
}

/**
 * A `<select>` you can type into.
 *
 * A native select is unusable past a few dozen entries, and the broker offers
 * 7391 symbols. `onSearch` hands the typed text back so the caller can filter
 * server-side; without it the filtering happens here over `choices`.
 */
export function SearchSelect({
  value,
  choices,
  onChange,
  onSearch,
  placeholder = 'Search…',
  emptyLabel = 'nothing matches',
  loading = false,
  disabled = false,
}: {
  value: string;
  choices: Choice[];
  onChange: (v: string) => void;
  onSearch?: (q: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  loading?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);

  // Close on any click that is not ours. Without this the list stays open
  // behind the next field the operator moves to.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const shown = useMemo(() => {
    if (onSearch) return choices; // already filtered by whoever fetched them
    const needle = q.trim().toLowerCase();
    if (!needle) return choices;
    return choices.filter(
      (c) =>
        c.label.toLowerCase().includes(needle) ||
        c.value.toLowerCase().includes(needle) ||
        (c.hint ?? '').toLowerCase().includes(needle),
    );
  }, [choices, q, onSearch]);

  const selected = choices.find((c) => c.value === value);
  const base =
    'w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500';

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(base, 'flex items-center justify-between text-left disabled:opacity-50')}
      >
        <span className={selected ? 'text-slate-200' : 'text-slate-500'}>
          {selected ? selected.label : '—'}
        </span>
        <span className="ml-2 shrink-0 text-slate-500">▾</span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded border border-edge bg-surface shadow-lg">
          <input
            autoFocus
            value={q}
            placeholder={placeholder}
            onChange={(e) => {
              setQ(e.target.value);
              onSearch?.(e.target.value);
            }}
            className="w-full border-b border-edge bg-transparent px-2 py-1.5 text-sm outline-none"
          />
          <ul className="max-h-64 overflow-y-auto py-1">
            {loading && <li className="px-2 py-1.5 text-xs text-slate-500">loading…</li>}
            {!loading && shown.length === 0 && (
              <li className="px-2 py-1.5 text-xs text-slate-500">{emptyLabel}</li>
            )}
            {shown.map((c) => (
              <li key={c.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(c.value);
                    setOpen(false);
                    setQ('');
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-sm hover:bg-slate-800',
                    c.value === value && 'bg-slate-800',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-slate-200">{c.label}</span>
                    {c.hint && <span className="block truncate text-xs text-slate-500">{c.hint}</span>}
                  </span>
                  {c.tag && <span className="shrink-0 text-xs text-amber-400">{c.tag}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function KeyValue({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1.5 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-slate-500">{k}</dt>
          <dd className="text-slate-200 tabular-nums break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
